import { Drawer } from '@/components/commons';
import { AgentService } from '@/services';
import { ENDPOINT } from '@/services/endpoint';
import { IResponseError } from '@/types/common';
import { IAgentPromptActive } from '@/types/response';
import { notificationMessage, NOTIFY_STATUS } from '@/utils';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface IProps {
  title?: string;
}

export const AgentPromptList: React.FC<IProps> = ({ title }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // ?key=xxx で来たら該当 key の履歴 Drawer を自動オープン（フロー解説からの遷移）
  useEffect(() => {
    const key = searchParams.get('key');
    if (key) {
      setSelectedKey(key);
    }
  }, [searchParams]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<{
    key: string;
    template: string;
    description: string;
  }>({ key: '', template: '', description: '' });

  // 全キーの ACTIVE 一覧（未seedキーは source='code' で補完される）
  const { data: activeData, isFetching: isFetchingActive } = useQuery(
    [ENDPOINT.AGENT.LIST_PROMPTS, 'active'],
    () => AgentService.listActivePrompts(),
  );

  // selectedKey の全 version 履歴
  const {
    data: versionsData,
    isFetching: isFetchingVersions,
    refetch: refetchVersions,
  } = useQuery(
    [ENDPOINT.AGENT.GET_PROMPT_BY_KEY, selectedKey],
    () => AgentService.getPromptVersions(selectedKey!),
    { enabled: !!selectedKey },
  );

  const selectedRow = (activeData?.data ?? []).find(
    (r) => r.key === selectedKey,
  );

  const activateMutation = useMutation(
    (params: { key: string; version: number }) =>
      AgentService.activatePromptVersion(params.key, {
        version: params.version,
      }),
    {
      onSuccess: () => {
        notificationMessage({
          message: 'ACTIVE を切り替えました',
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetchVersions();
        queryClient.invalidateQueries([ENDPOINT.AGENT.LIST_PROMPTS, 'active']);
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `切替に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const createMutation = useMutation(
    () =>
      AgentService.createPromptVersion({
        key: createForm.key,
        template: createForm.template,
        description: createForm.description || undefined,
        activate: true,
      }),
    {
      onSuccess: (response) => {
        notificationMessage({
          message: `新バージョン v${response.data.version} を ACTIVE で作成しました`,
          type: NOTIFY_STATUS.SUCCESS,
        });
        setCreateOpen(false);
        setCreateForm({ key: '', template: '', description: '' });
        queryClient.invalidateQueries([ENDPOINT.AGENT.LIST_PROMPTS, 'active']);
        if (selectedKey === response.data.key) {
          refetchVersions();
        }
      },
      onError: (error: IResponseError) => {
        notificationMessage({
          message: `作成に失敗: ${error.message ?? 'unknown error'}`,
          type: NOTIFY_STATUS.ERROR,
        });
      },
    },
  );

  const handleActivate = (key: string, version: number) => {
    Modal.confirm({
      title: `key=${key} を v${version} に切り替えますか？`,
      content: '次回以降の生成で新しい version が使われます',
      okText: 'ACTIVE にする',
      cancelText: 'キャンセル',
      onOk: () => activateMutation.mutate({ key, version }),
    });
  };

  /** 「この key を改版」: 現在の ACTIVE（or コード版）を初期値にしてモーダルを開く */
  const openReviseModal = (row: IAgentPromptActive) => {
    setCreateForm({
      key: row.key,
      template: row.template,
      description: '',
    });
    setCreateOpen(true);
  };

  const activeColumns: TableColumnsType<IAgentPromptActive> = [
    {
      title: 'key',
      dataIndex: 'key',
      width: 220,
      render: (key: string) => <Text strong>{key}</Text>,
    },
    {
      title: 'version',
      dataIndex: 'version',
      width: 110,
      render: (v: number | 'code', r) =>
        r.source === 'code' ? (
          <Tag color='default'>コード内定義</Tag>
        ) : (
          <Tag color='blue'>v{v}</Tag>
        ),
    },
    {
      title: 'description',
      dataIndex: 'description',
      ellipsis: true,
      render: (d?: string | null) => d || <Text type='secondary'>(なし)</Text>,
    },
    {
      title: 'placeholders',
      dataIndex: 'placeholders',
      width: 320,
      render: (ps?: string[]) =>
        ps && ps.length > 0 ? (
          <Space wrap size={[4, 4]}>
            {ps.map((p) => (
              <Tag key={p} color='geekblue'>{`{{${p}}}`}</Tag>
            ))}
          </Space>
        ) : (
          <Text type='secondary'>(なし)</Text>
        ),
    },
    {
      title: 'updatedAt',
      dataIndex: 'updatedAt',
      width: 170,
      render: (t: string | null) =>
        t ? new Date(t).toLocaleString('ja-JP') : '-',
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <Button
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            openReviseModal(r);
          }}
        >
          改版
        </Button>
      ),
    },
  ];

  const renderVersionCards = () => {
    if (isFetchingVersions) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin />
        </div>
      );
    }
    const versions = versionsData?.data ?? [];
    if (versions.length === 0) {
      return (
        <Empty
          description={
            selectedRow?.source === 'code'
              ? 'DB 未登録（コード内デフォルトを使用中）。「改版」から投入するか seed:agent:prompts を実行してください'
              : 'この key の version はありません'
          }
        />
      );
    }
    return (
      <Space direction='vertical' size={16} style={{ width: '100%' }}>
        {versions.map((v) => (
          <Card
            key={v.id}
            size='small'
            style={{
              borderColor: v.isActive ? '#52c41a' : undefined,
              borderWidth: v.isActive ? 2 : 1,
            }}
            title={
              <Space>
                <Tag color={v.isActive ? 'green' : 'default'}>v{v.version}</Tag>
                {v.isActive && <Tag color='green'>ACTIVE</Tag>}
                <Text type='secondary' style={{ fontSize: 12 }}>
                  {new Date(v.createdAt).toLocaleString('ja-JP')}
                </Text>
              </Space>
            }
            extra={
              v.isActive ? null : (
                <Button
                  size='small'
                  onClick={() => handleActivate(v.key, v.version)}
                  loading={
                    activateMutation.isLoading &&
                    activateMutation.variables?.version === v.version
                  }
                >
                  この version を ACTIVE にする
                </Button>
              )
            }
          >
            <div style={{ marginBottom: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                description
              </Text>
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                {v.description || <Text type='secondary'>(なし)</Text>}
              </Paragraph>
            </div>
            {v.placeholders && v.placeholders.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ display: 'block', marginBottom: 4 }}>
                  placeholders
                </Text>
                <Space wrap size={[4, 4]}>
                  {v.placeholders.map((p) => (
                    <Tag key={p} color='geekblue'>{`{{${p}}}`}</Tag>
                  ))}
                </Space>
              </div>
            )}
            <Collapse
              size='small'
              defaultActiveKey={v.isActive ? ['template'] : undefined}
              items={[
                {
                  key: 'template',
                  label: (
                    <Text strong>
                      template ({v.template.length.toLocaleString()} 文字)
                    </Text>
                  ),
                  children: (
                    <pre
                      style={{
                        background: '#fafafa',
                        padding: 12,
                        borderRadius: 4,
                        fontSize: 12,
                        maxHeight: 600,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        margin: 0,
                      }}
                    >
                      {v.template}
                    </pre>
                  ),
                },
              ]}
            />
          </Card>
        ))}
      </Space>
    );
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header>
          <Title level={3} style={{ marginTop: 0 }}>
            AIエージェント プロンプト管理
          </Title>
          <Paragraph type='secondary'>
            エンジン本文・共通ルール（フクマル人格）・検索結果注入ブロックのテンプレート一覧です。
            行をクリックすると version 履歴とロールバック操作ができます。
            「コード内定義」の key はまだ DB 未登録で、サーバー内のデフォルトが使われています。
          </Paragraph>
        </header>

        <Alert
          type='info'
          showIcon
          message='新 version を ACTIVE にすると次回以降の生成で反映されます。使用された version は生成一覧の生成ログ（promptVersions）から辿れます'
        />

        <Card
          extra={
            <Button type='primary' onClick={() => setCreateOpen(true)}>
              新規 / 改版
            </Button>
          }
          title='ACTIVE プロンプト'
        >
          <Table<IAgentPromptActive>
            rowKey='key'
            loading={isFetchingActive}
            columns={activeColumns}
            dataSource={activeData?.data ?? []}
            pagination={false}
            onRow={(record) => ({
              onClick: () => setSelectedKey(record.key),
              style: { cursor: 'pointer' },
            })}
            locale={{ emptyText: 'プロンプトはまだありません' }}
          />
        </Card>
      </div>

      {/* version 履歴ドロワー */}
      <Drawer
        title={selectedKey ? `version 履歴: ${selectedKey}` : ''}
        open={!!selectedKey}
        onCancel={() => {
          setSelectedKey(null);
          if (searchParams.get('key')) {
            searchParams.delete('key');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onlyCancel
        cancelText='閉じる'
        width={1100}
      >
        {renderVersionCards()}
      </Drawer>

      {/* 新規 / 改版モーダル */}
      <Modal
        title='プロンプト新規 / 改版'
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createMutation.mutate()}
        confirmLoading={createMutation.isLoading}
        okText='ACTIVE で作成'
        cancelText='キャンセル'
        width={800}
      >
        <Form layout='vertical'>
          <Form.Item label='key' required>
            <Input
              placeholder='例: outing.main'
              value={createForm.key}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, key: e.target.value }))
              }
            />
          </Form.Item>
          <Form.Item label='description'>
            <Input
              placeholder='例: v2: 信頼性ルールを強化'
              value={createForm.description}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, description: e.target.value }))
              }
            />
          </Form.Item>
          <Form.Item label='template' required>
            <TextArea
              placeholder='プロンプト本文。{{var}} で変数参照'
              autoSize={{ minRows: 12, maxRows: 24 }}
              value={createForm.template}
              onChange={(e) =>
                setCreateForm((f) => ({ ...f, template: e.target.value }))
              }
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
